import React, { useMemo, useState, useCallback, useEffect } from 'react'
import { AgGridReact } from 'ag-grid-react'
import {
  ColDef,
  ValueFormatterParams,
  ICellRendererParams,
} from 'ag-grid-community'
import { IconButton, Box, Typography, Tooltip, useTheme } from '@mui/material'
import HistoryIcon from '@mui/icons-material/History'
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { BidStatus } from '../../shared/types'

import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-quartz.css'
import { formatDateTime, formatPercentage } from '../../shared/formats'

// Empty array constant to avoid re-renders
const EMPTY_SELECTED_ROWS: BidTableRow[] = []

interface ParticipationData {
  total: number
  responded: number
  pendingIssuers: string[]
}

// Local interface for table rows
interface BidTableRow {
  bidId: string
  scenarioId: string // Direct scenario ID for reliable lookups
  issuerTenantId: string // Direct issuer tenant ID for reliable lookups
  scenarioName: string
  issuerName: string
  rate: number | null
  treasuryRate: number | null
  sotr: number | null // Calculated: rate - treasuryRate
  issuerComments: string | null
  notes: string | null
  bidTime: string | null
  bidStatus: BidStatus | null
}

interface BidsTableProps {
  ref?: React.RefObject<AgGridReact | null>
  bids: BidTableRow[]
  isLoading?: boolean
  showCheckboxes?: boolean // Controls whether checkboxes are shown for row selection
  selectedRows?: BidTableRow[] // Currently selected rows for validation
  onSelectionChanged?: (validatedRows: BidTableRow[]) => void // Callback when selection changes with validated rows
  participationData?: ParticipationData // Data about which issuers have not responded
}

interface ParticipationPanelProps {
  participationData: ParticipationData
}

const ParticipationPanel: React.FC<ParticipationPanelProps> = ({
  participationData,
}) => {
  const theme = useTheme()

  const pendingIssuers = participationData.pendingIssuers

  const tooltipContent =
    pendingIssuers.length > 0 ? (
      <Box sx={{ p: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Did Not Respond
        </Typography>
        <Box
          component="ul"
          sx={{
            pl: 2,
            m: 0,
            maxHeight: '200px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.primary.light,
              borderRadius: '3px',
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
              },
            },
          }}
        >
          {pendingIssuers.map((issuerName) => (
            <Typography
              component="li"
              key={issuerName}
              variant="body2"
              sx={{ mb: 0.5, fontSize: '0.875rem' }}
            >
              {issuerName}
            </Typography>
          ))}
        </Box>
      </Box>
    ) : null

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        backgroundColor: theme.palette.grey['50'],
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
      }}
    >
      <Typography variant="body2" sx={{ color: theme.palette.grey['700'] }}>
        {participationData.responded} of {participationData.total} issuers have
        responded
      </Typography>
      {pendingIssuers.length > 0 && (
        <Tooltip
          title={tooltipContent}
          arrow
          placement="bottom-start"
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: 'background.paper',
                color: 'text.primary',
                border: '1px solid',
                borderColor: theme.palette.primary.light,
                boxShadow: 'none',
                maxWidth: 300,
                '& .MuiTooltip-arrow': {
                  color: theme.palette.primary.light,
                },
              },
            },
          }}
        >
          <InfoOutlinedIcon
            fontSize="small"
            sx={{ color: 'text.secondary', cursor: 'help' }}
          />
        </Tooltip>
      )}
    </Box>
  )
}

// Separate component for the History cell to avoid hook issues in cellRenderer
interface HistoryCellProps {
  scenarioName: string
  issuerName: string
  allBids: BidTableRow[]
  isExpanded: boolean
  onToggle: (scenarioName: string, issuerName: string) => void
}

const HistoryCell: React.FC<HistoryCellProps> = ({
  scenarioName,
  issuerName,
  allBids,
  isExpanded,
  onToggle,
}) => {
  // Count historical bids for this combination
  const historicalCount =
    allBids.filter(
      (bid) =>
        bid.scenarioName === scenarioName && bid.issuerName === issuerName
    ).length - 1 // Subtract 1 for the current/most recent bid

  // Only show icon if there are historical bids
  if (historicalCount <= 0) {
    return null
  }

  return (
    <IconButton
      size="small"
      onClick={() => onToggle(scenarioName, issuerName)}
      title={
        isExpanded
          ? 'Hide history'
          : `Show ${historicalCount} historical bid${historicalCount > 1 ? 's' : ''}`
      }
    >
      {isExpanded ? <HistoryToggleOffIcon /> : <HistoryIcon />}
    </IconButton>
  )
}

export const BidsTable = ({
  ref,
  bids,
  isLoading = false,
  showCheckboxes = true,
  selectedRows = EMPTY_SELECTED_ROWS,
  onSelectionChanged,
  participationData,
}: BidsTableProps) => {
  const theme = useTheme()

  // Track which scenario/issuer combinations have their history expanded
  const [expandedHistories, setExpandedHistories] = useState<Set<string>>(
    new Set()
  )

  // Selection change handler for AG-Grid
  const handleSelectionChange = useCallback(() => {
    if (onSelectionChanged && ref?.current?.api) {
      const selectedRows = ref.current.api.getSelectedRows() as BidTableRow[]
      onSelectionChanged(selectedRows)
    }
  }, [onSelectionChanged, ref])

  // Force AG-Grid to refresh row selectability when selectedRows changes
  useEffect(() => {
    if (ref?.current?.api && showCheckboxes) {
      // Force AG-Grid to re-evaluate row selectability by redrawing rows
      ref.current.api.redrawRows()
    }
  }, [selectedRows, ref, showCheckboxes])

  // Toggle history visibility for a specific scenario/issuer combination
  const toggleHistory = useCallback(
    (scenarioName: string, issuerName: string) => {
      const key = `${scenarioName}_${issuerName}`
      setExpandedHistories((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(key)) {
          newSet.delete(key)
        } else {
          newSet.add(key)
        }
        return newSet
      })
    },
    []
  )

  // Filter bids: show most recent + historical bids for expanded combinations
  const { filteredBids, mostRecentBidIds } = useMemo(() => {
    const bidMap = new Map<string, BidTableRow[]>()
    const mostRecentIds = new Set<string>()

    // Group all bids by scenarioName + issuerName
    bids.forEach((bid) => {
      const key = `${bid.scenarioName}_${bid.issuerName}`
      if (!bidMap.has(key)) {
        bidMap.set(key, [])
      }
      bidMap.get(key)!.push(bid)
    })

    const result: BidTableRow[] = []

    bidMap.forEach((bidsGroup, key) => {
      // Sort bids by bidTime (most recent first)
      const sortedBids = bidsGroup.sort((a, b) => {
        const aTime = a.bidTime ? new Date(a.bidTime).getTime() : 0
        const bTime = b.bidTime ? new Date(b.bidTime).getTime() : 0
        return bTime - aTime
      })

      // Always include the most recent bid
      if (sortedBids.length > 0) {
        const mostRecentBid = sortedBids[0]
        mostRecentIds.add(mostRecentBid.bidId)
        result.push(mostRecentBid)

        // Include historical bids if this combination is expanded
        if (expandedHistories.has(key) && sortedBids.length > 1) {
          result.push(...sortedBids.slice(1))
        }
      }
    })

    return { filteredBids: result, mostRecentBidIds: mostRecentIds }
  }, [bids, expandedHistories])

  // Column definitions for ag-grid
  const columnDefs: ColDef<BidTableRow>[] = useMemo(
    () => [
      {
        headerName: 'Scenario',
        field: 'scenarioName',
        sortable: true,
        filter: true,
        width: 120,
      },
      {
        headerName: 'Issuer',
        field: 'issuerName',
        sortable: true,
        filter: true,
        width: 150,
      },
      {
        headerName: 'Rate',
        field: 'rate',
        sortable: true,
        filter: 'agNumberColumnFilter',
        width: 100,
        valueFormatter: (
          params: ValueFormatterParams<BidTableRow, number | null>
        ) => formatPercentage(params.value),
      },
      {
        headerName: 'Treasury Rate',
        field: 'treasuryRate',
        sortable: true,
        filter: 'agNumberColumnFilter',
        width: 130,
        valueFormatter: (
          params: ValueFormatterParams<BidTableRow, number | null>
        ) => formatPercentage(params.value),
      },
      {
        headerName: 'SOTR',
        field: 'sotr',
        sortable: true,
        filter: 'agNumberColumnFilter',
        width: 100,
        valueFormatter: (
          params: ValueFormatterParams<BidTableRow, number | null>
        ) => formatPercentage(params.value),
      },
      {
        headerName: 'Issuer Comments',
        field: 'issuerComments',
        sortable: true,
        filter: true,
        width: 200,
        cellRenderer: (
          params: ICellRendererParams<BidTableRow, string | null>
        ) => params.value || '-',
      },
      {
        headerName: 'Bid Time',
        field: 'bidTime',
        sortable: true,
        filter: 'agDateColumnFilter',
        width: 150,
        valueFormatter: (
          params: ValueFormatterParams<BidTableRow, string | null>
        ) => formatDateTime(params.value),
      },
      {
        headerName: 'History',
        field: 'bidId', // Use bidId as field but this column shows history controls
        sortable: false,
        filter: false,
        width: 80,
        cellRenderer: (params: ICellRendererParams<BidTableRow>) => {
          const { scenarioName, issuerName } = params.data!
          const key = `${scenarioName}_${issuerName}`
          const isExpanded = expandedHistories.has(key)

          return (
            <HistoryCell
              scenarioName={scenarioName}
              issuerName={issuerName}
              allBids={bids}
              isExpanded={isExpanded}
              onToggle={toggleHistory}
            />
          )
        },
      },
    ],
    [bids, expandedHistories, toggleHistory]
  )

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
    }),
    []
  )

  return (
    <>
      <style>
        {`
          .ag-row .ag-cell {
            display: flex;
            align-items: center;
          }
          
          .ag-checkbox-disabled {
            opacity: 0.3;
            cursor: not-allowed;
            pointer-events: none;
          }
        `}
      </style>

      {participationData && (
        <ParticipationPanel participationData={participationData} />
      )}

      <Box
        id="bids-table"
        className="ag-theme-quartz-auto-dark ag-theme-cwan compact-grid"
        sx={{
          minHeight: '200px',
          maxHeight: '600px',
          width: '100%',
          border: `1px solid ${theme.palette.grey[200]}`,
          overflow: 'auto',
        }}
      >
        <AgGridReact<BidTableRow>
          ref={ref}
          theme="legacy" // Use legacy theming for v32 CSS compatibility
          rowData={filteredBids}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          loading={isLoading}
          animateRows={true}
          suppressCellFocus={true}
          rowHeight={40} // Ensure adequate height for History column icons
          getRowId={(params) => params.data.bidId} // Use bidId for row identification
          getRowClass={(params) => {
            const row = params.data
            if (!row || !showCheckboxes) return ''

            return ''
          }}
          {...(showCheckboxes && {
            rowSelection: {
              mode: 'multiRow',
              checkboxes: true,
              headerCheckbox: false, // Disable header checkbox to prevent selecting all rows
              enableClickSelection: false, // only allow checkbox selection
              hideDisabledCheckboxes: true,
              isRowSelectable: (params) => {
                const row = params.data
                if (!row) return false

                // Historical rows (not most recent) should not be selectable
                if (!mostRecentBidIds.has(row.bidId)) {
                  return false
                }

                return true
              },
            },
            onSelectionChanged: handleSelectionChange,
          })}
          tooltipShowDelay={500}
          tooltipHideDelay={1000}
          domLayout="autoHeight"
        />
      </Box>
    </>
  )
}

BidsTable.displayName = 'BidsTable'

export type { BidTableRow }
